import { FC } from 'react';
import { Button } from '@shared/ui/kit';
import stylex from '@stylexjs/stylex';
import { styles } from './styles';
import { ChevronLeftIcon } from '@shared/assets/icons';
import { StepInfo } from '@features/modal-invitation/model/types';

interface Counts {
  email: number;
  sms: number;
}

interface StepSummaryProps {
  usersSMS: { users: number[]; tickets: number[]; isAdmin: boolean };
  invitationCounts: Counts;
  ticketCounts: Counts;
  shouldShowTicketsSection: boolean;
  totalEmail: number;
  totalSMS: number;
  totalSMSCost: number;
  handleSendInvitations: () => void;
  handleBack: () => void;
  stepInfo: StepInfo;
  invitationExpiresAt: Date | null;
  sendTicketAfterAcceptAt: Date | null;
}

export const StepSummary: FC<StepSummaryProps> = ({
  usersSMS,
  invitationCounts,
  ticketCounts,
  shouldShowTicketsSection,
  totalEmail,
  totalSMS,
  totalSMSCost,
  handleSendInvitations,
  handleBack,
  stepInfo,
  invitationExpiresAt,
  sendTicketAfterAcceptAt,
}) => {
  const formatDate = (date: Date, time: string) => {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year} ${time}`;
  };
  return (
    <>
      <div {...stylex.props(styles.statsWrapper)}>
        <div {...stylex.props(styles.statsContent)}>
          <div {...stylex.props(styles.information)}>
            {shouldShowTicketsSection
              ? `Будет отправлено ${usersSMS?.tickets.length} приглашений и ${usersSMS?.tickets.length} билетов`
              : `Будет отправлено ${usersSMS?.tickets.length} приглашений`}
          </div>
          <div {...stylex.props(styles.groupStats)}>
            <div {...stylex.props(styles.groupStatsTitle)}>
              <div {...stylex.props(styles.groupStatsText)}>Приглашения</div>
            </div>
            {invitationCounts.email > 0 && (
              <div {...stylex.props(styles.groupStatsEmailBlock)}>
                <div {...stylex.props(styles.groupStatsEmail)}>
                  <div {...stylex.props(styles.groupStatsEmailTitle)}>E-mail:</div>
                </div>
                <div {...stylex.props(styles.groupStatsEmailLine)}></div>
                <div {...stylex.props(styles.groupStatsEmailCount)}>{invitationCounts.email}</div>
              </div>
            )}
            {invitationCounts.sms > 0 && (
              <div {...stylex.props(styles.groupStatsEmailBlock)}>
                <div {...stylex.props(styles.groupStatsEmail)}>
                  <div {...stylex.props(styles.groupStatsEmailTitle)}>СМС:</div>
                </div>
                <div {...stylex.props(styles.groupStatsEmailLine)}></div>
                <div {...stylex.props(styles.groupStatsEmailCount)}>
                  {invitationCounts.sms} (~ {invitationCounts.sms * 5} руб.)
                </div>
              </div>
            )}
          </div>

          {shouldShowTicketsSection && (
            <div {...stylex.props(styles.groupStats)}>
              <div {...stylex.props(styles.groupStatsTitle)}>
                <div {...stylex.props(styles.groupStatsText)}>Билеты</div>
              </div>
              {ticketCounts.email > 0 && (
                <div {...stylex.props(styles.groupStatsEmailBlock)}>
                  <div {...stylex.props(styles.groupStatsEmail)}>
                    <div {...stylex.props(styles.groupStatsEmailTitle)}>E-mail:</div>
                  </div>
                  <div {...stylex.props(styles.groupStatsEmailLine)}></div>
                  <div {...stylex.props(styles.groupStatsEmailCount)}>{ticketCounts.email}</div>
                </div>
              )}
              {ticketCounts.sms > 0 && (
                <div {...stylex.props(styles.groupStatsEmailBlock)}>
                  <div {...stylex.props(styles.groupStatsEmail)}>
                    <div {...stylex.props(styles.groupStatsEmailTitle)}>СМС:</div>
                  </div>
                  <div {...stylex.props(styles.groupStatsEmailLine)}></div>
                  <div {...stylex.props(styles.groupStatsEmailCount)}>
                    {ticketCounts.sms} (~ {ticketCounts.sms * 5} руб.)
                  </div>
                </div>
              )}
            </div>
          )}

          <div {...stylex.props(styles.groupStats)}>
            <div {...stylex.props(styles.groupStatsTitle)}>
              <div {...stylex.props(styles.groupStatsText)}>Всего</div>
            </div>
            {totalEmail > 0 && (
              <div {...stylex.props(styles.groupStatsEmailBlock)}>
                <div {...stylex.props(styles.groupStatsEmail)}>
                  <div {...stylex.props(styles.groupStatsEmailTitle)}>E-mail:</div>
                </div>
                <div {...stylex.props(styles.groupStatsEmailLine)}></div>
                <div {...stylex.props(styles.groupStatsEmailCount)}>{totalEmail}</div>
              </div>
            )}
            {totalSMS > 0 && (
              <div {...stylex.props(styles.groupStatsEmailBlock)}>
                <div {...stylex.props(styles.groupStatsEmail)}>
                  <div {...stylex.props(styles.groupStatsEmailTitle)}>СМС:</div>
                </div>
                <div {...stylex.props(styles.groupStatsEmailLine)}></div>
                <div {...stylex.props(styles.groupStatsEmailCount)}>
                  {totalSMS} (~ {totalSMSCost} руб.)
                </div>
              </div>
            )}
          </div>
        </div>

        <div {...stylex.props(styles.dateWrapper)}>
          <div {...stylex.props(styles.dateInviteTitle)}>
            <div {...stylex.props(styles.dateInviteText)}>
              <div {...stylex.props(styles.datePeriodText)}>Срок действия приглашений</div>
            </div>
            <div {...stylex.props(styles.dateLine)}></div>
            <div {...stylex.props(styles.datePeriod)}>
              {invitationExpiresAt ? `по ${formatDate(invitationExpiresAt, '23:59')}` : 'Не выбрано'}
            </div>
          </div>

          {shouldShowTicketsSection && (
            <div {...stylex.props(styles.dateInviteTitle)}>
              <div {...stylex.props(styles.dateInviteText)}>
                <div {...stylex.props(styles.datePeriodText)}>Дата отправки билетов</div>
              </div>
              <div {...stylex.props(styles.dateLine)}></div>
              <div {...stylex.props(styles.datePeriod)}>
                {sendTicketAfterAcceptAt ? formatDate(sendTicketAfterAcceptAt, '11:00') : 'Не выбрано'}
              </div>
            </div>
          )}

          {/* <div {...stylex.props(styles.alertContainer)}>
            <img src={info} alt="Информация по отправке" />
            <div {...stylex.props(styles.alertText)}>Билеты будут отправлены участникам подтвердившим приглашение</div>
          </div> */}
        </div>
      </div>

      <div {...stylex.props(styles.actions)}>
        <div {...stylex.props(styles.stepsContainer)}>
          <Button size="small" variant="secondary" onClick={handleBack}>
            <ChevronLeftIcon />
          </Button>
          <div {...stylex.props(styles.stepInfo)}>
            <div {...stylex.props(styles.step)}>
              Шаг {stepInfo.current} из {stepInfo.total}
            </div>
            <div {...stylex.props(styles.info)}>
              <span {...stylex.props(styles.nextStep)}>{stepInfo.next}</span>
            </div>
          </div>
        </div>

        <Button onClick={handleSendInvitations} variant="accent" settings={{ padding: '8px 20px' }} size="small">
          Отправить
        </Button>
      </div>
    </>
  );
};
